/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import React, { useEffect, useState, useRef } from 'react';
import { di } from 'react-magnetic-di';
import { getIframeSandboxAttribute } from '../../../utils';
import { IframeDwellTracker } from './IframeDwellTracker';
import { IFrame } from './IFrame';
function mergeRefs(refs) {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null) {
        ref.current = value;
      }
    });
  };
}
const iframeStyles = css({
  border: 0,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '3px'
});
export const Frame = /*#__PURE__*/React.forwardRef(({
  url,
  isTrusted = false,
  testId,
  onIframeDwell,
  onIframeFocus,
  title
}, iframeRef) => {
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const [isMouseOver, setMouseOver] = useState(false);
  const [isWindowFocused, setWindowFocused] = useState(true);
  const ref = useRef();
  const mergedRef = mergeRefs([iframeRef, ref]);
  const [percentVisible, setPercentVisible] = useState(0);

  /**
   * These are the 'percent visible' thresholds at which the intersectionObserver will
   * trigger a state change. Eg. when the user scrolls and moves from 74% to 76%, or
   * vice versa. It's in a state object so that its static for the useEffect
   */
  const [threshold] = useState([0.75, 0.8, 0.85, 0.9, 0.95, 1]);
  useEffect(() => {
    if (!ref || !ref.current) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        setPercentVisible(entry === null || entry === void 0 ? void 0 : entry.intersectionRatio);
      });
    }, {
      threshold
    });
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [threshold, mergedRef]);
  useEffect(() => {
    const onBlur = () => {
      setWindowFocused(false);
      if (document.activeElement === ref.current) {
        onIframeFocus && onIframeFocus();
      }
    };
    const onFocus = () => {
      setWindowFocused(true);
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [ref, onIframeFocus]);
  if (!url) {
    return null;
  }
  return jsx(React.Fragment, null, jsx(IframeDwellTracker, {
    isIframeLoaded: isIframeLoaded,
    isMouseOver: isMouseOver,
    isWindowFocused: isWindowFocused,
    iframePercentVisible: percentVisible,
    onIframeDwell: onIframeDwell
  }), jsx(IFrame, {
    childRef: mergedRef,
    src: url,
    "data-testid": `${testId}-frame`,
    "data-iframe-loaded": isIframeLoaded,
    css: iframeStyles,
    onMouseEnter: () => setMouseOver(true),
    onMouseLeave: () => setMouseOver(false),
    allowFullScreen: true,
    scrolling: "yes",
    allow: "autoplay; encrypted-media; clipboard-write",
    onLoad: () => {
      setIframeLoaded(true);
    },
    sandbox: getIframeSandboxAttribute(isTrusted),
    title: title
  }));
});